import React from 'react';
import dateWindowMatch from '@/utils/date/date.window.match';

export function useListFilters(defaultSort = '', defaultDescending = true) {
    const [search, setSearch] = React.useState('');
    const [active, setActive] = React.useState<Record<string, string[]>>({});
    const [dates, setDates] = React.useState<Record<string, string>>({});
    const [excluded, setExcludedState] = React.useState<Record<string, boolean>>({});
    const [sort, setSort] = React.useState(defaultSort);
    const [descending, setDescending] = React.useState(defaultDescending);

    const toggle = (sectionId: string, value: string) => setActive((current) => {
        const values = current[sectionId] ?? [];
        const next = values.includes(value) ? values.filter((item) => item !== value) : [...values, value];
        const copy = { ...current, [sectionId]: next };
        if (next.length === 0) delete copy[sectionId];
        return copy;
    });

    const setDate = (sectionId: string, value: string) => setDates((current) => {
        const copy = { ...current };
        if (!value || (current[sectionId] === value && !value.startsWith('range:'))) delete copy[sectionId];
        else copy[sectionId] = value;
        return copy;
    });

    const setExcluded = (sectionId: string, value: boolean) => setExcludedState((current) => {
        if (Boolean(current[sectionId]) === value) return current;
        const copy = { ...current };
        if (value) copy[sectionId] = true;
        else delete copy[sectionId];
        return copy;
    });

    const clear = () => { setActive({}); setDates({}); setExcludedState({}); };

    const passes = (sectionId: string, value: string) => {
        const values = active[sectionId];
        return !values || values.length === 0 || values.includes(value);
    };

    const passesAny = (sectionId: string, values: string[]) => {
        const selected = active[sectionId];
        if (!selected || selected.length === 0) return true;
        return values.some((value) => selected.includes(value));
    };

    const passesDate = (sectionId: string, iso: string | null | undefined) => dateWindowMatch(dates[sectionId] ?? '', iso);

    const matches = (haystack: string) => haystack.toLowerCase().includes(search.trim().toLowerCase());

    const toggleDirection = () => setDescending((current) => !current);

    const activeCount = Object.values(active).reduce((sum, values) => sum + values.length, 0) + Object.keys(dates).length;

    return {
        search, setSearch,
        active, toggle, passes, passesAny,
        excluded, setExcluded,
        dates, setDate, passesDate,
        clear, matches,
        sort, setSort, descending, toggleDirection,
        activeCount,
    };
}
